/**
 * Add to Cart component
 * * @module component-addtocart
 * @version 2.0.0
 * @extends HTMLElement
 */
class addToCartComponent extends HTMLElement {

    /**
     * Add to Cart web component.
     * * @constructor
     */
    constructor() {
        super();

        if (this.dataset.handle) {
            window.theme.product.loadProduct(this.dataset.handle, true).then(product => {
                this.initSubmitButton(product);
                this.initEvents(product);
                // Call our new function after initialization
                this.updateButtonStateBasedOnSizeSelection();
            });
        } else {
            console.log('Missing product handle', this);
        }
    }

    /**
     * Init component events.
     * * @param {object} product Product data.
     * @returns {undefined}
     */
    initEvents(product) {
        // On submit add to cart form
        const form = this.getForm();
        if (form) {
            // Modify the form submit listener to include our validation first
            form.addEventListener('submit', event => this.onFormSubmit(event));
        }

        // On variant selector ready
        const variantSelector = document.querySelector('variant-selector[data-handle="' + this.dataset.handle + '"]');
        if (variantSelector) {
            variantSelector.addEventListener('ready', event => this.OnVariantSelectorReady(event, product));

            if (variantSelector.classList.contains('ready')) {
                this.OnVariantSelectorChange(variantSelector.querySelector('select'), product);
            }

            // Also listen to changes on the variant selector itself, which should trigger our button update
            // Note: Your theme already has an addEventListener for 'change' inside OnVariantSelectorChange.
            // We need to ensure our button state update runs after every relevant change.
            // Let's add a specific listener for radio button changes here, as your variant selector HTML is radio buttons.
            const sizeRadios = this.querySelectorAll('input[type="radio"][name="Size"]');
            sizeRadios.forEach(radio => {
                radio.addEventListener('change', () => this.updateButtonStateBasedOnSizeSelection());
            });
        }
    }

    /**
     * Toggle button if product select variant is out of stock.
     * This existing function handles stock, we will layer our size check on top.
     * * @param {object} select Product select variant.
     * @returns {undefined}
     */
    toggleButton(select) {
        let optionSelected = select.children[select.selectedIndex],
            outOfStockContent = this.querySelector('[data-outofstock-content]'),
            btn = this.getSubmitButton(),
            optionSelectedID = '',
            form = this.querySelector('form');

        if (optionSelected) {
            if (optionSelected.dataset.disabled === '') {
                optionSelected = false;
            } else {
                optionSelectedID = optionSelected.dataset.variantId;
                optionSelected = true;
            }
        } else {
            optionSelected = false;
        }

        // --- Original theme logic for stock (we keep this) ---
        if (optionSelected) {
            if (outOfStockContent)
                outOfStockContent.classList.remove('active');
            btn.disabled = false;
        } else {
            if (outOfStockContent)
                outOfStockContent.classList.add('active');
            btn.disabled = true;
        }
        // --- End Original theme logic ---

        if (form.dataset.editForm && form.dataset.editForm == optionSelectedID) {
            btn.disabled = true;
        }

        // Call our function here to update text based on size selection
        this.updateButtonStateBasedOnSizeSelection();
    }

    /**
     * Get component template
     * * @returns {string} Component HTML template.
     */
    getTemplate() {
        if (this.querySelector('button[type="submit"][data-edit-btn]') && this.querySelector('button[type="submit"][data-edit-btn]').innerHTML) {
            return this.querySelector('button[type="submit"]').innerHTML;
        }
        return window.theme.addToCart.template ? window.theme.addToCart.template : '';
    }

    /**
     * Init add to cart button features
     * * @param {object} product Product data.
     * @returns {undefined}
     */
    initSubmitButton(product) {
        let html = this.getTemplate(),
            btn = this.getSubmitButton();

        if (html && btn) {
            btn.classList.add('product-addtocart__button');
            btn.classList.add('btn-icon');
            btn.classList.add('btn-icon-before');
            btn.classList.add('btn-loading');

            if (product.available) {
                btn.classList.add('active');
                btn.disabled = false;
            } else {
                btn.disabled = true;
            }

            // Inject HTML
            btn.innerHTML = html;
        }
    }

    getForm() {
        return this.querySelector('form');
    }

    getSubmitButton() {
        return this.querySelector('button[type="submit"]');
    }

    onFormSubmit(event) {
        const form = event.target;

        // Our validation logic (now part of the component)
        const sizeRadios = this.querySelectorAll('input[type="radio"][name="Size"]');
        
        // --- START NEW LOGIC FOR HANDLING PRODUCTS WITH NO SIZE OPTIONS ---
        let isSizeOptionPresent = sizeRadios.length > 0; // Check if there are any size options at all
        let isSizeSelected = false;

        if (isSizeOptionPresent) {
            // If size options are present, check if one is actually selected
            for (let i = 0; i < sizeRadios.length; i++) {
                if (sizeRadios[i].checked) {
                    isSizeSelected = true;
                    break;
                }
            }
        } else {
            // If no size options are present, consider a size to be "selected" by default
            // (i.e., nothing for the user to choose, so it's ready to add to cart)
            isSizeSelected = true; 
        }
        // --- END NEW LOGIC FOR HANDLING PRODUCTS WITH NO SIZE OPTIONS ---

        if (!isSizeSelected) { // This condition now correctly factors in whether options exist
            event.preventDefault(); // Stop the form from submitting

            let errorMessageContainer = form.querySelector('.product-form__error-message');
            if (!errorMessageContainer) {
                errorMessageContainer = document.createElement('div');
                errorMessageContainer.className = 'product-form__error-message';
                errorMessageContainer.style.color = 'red';
                errorMessageContainer.style.marginTop = '10px';
                errorMessageContainer.style.marginBottom = '10px';
                errorMessageContainer.textContent = 'Please select a size.';
                
                const addToCartButton = this.getSubmitButton(); // Use getSubmitButton from the component
                if (addToCartButton) {
                    addToCartButton.parentNode.insertBefore(errorMessageContainer, addToCartButton);
                } else {
                    form.appendChild(errorMessageContainer);
                }
            } else {
                errorMessageContainer.textContent = 'Please select a size.';
                errorMessageContainer.style.display = 'block';
            }

            if (errorMessageContainer) {
                errorMessageContainer.scrollIntoView({ behavior: "smooth", block: "center" });
            }
            return; // Important: Stop here if size is not selected
        } else {
            const errorMessageContainer = form.querySelector('.product-form__error-message');
            if (errorMessageContainer) {
                errorMessageContainer.style.display = 'none';
            }
        }
        // --- End our validation logic ---


        event.preventDefault(); // Keep this original line
        
        let button = form.querySelector('[type="submit"]'),
            validate = this.validateFieldRequired(form),
            message = form.querySelector('[data-error-message]');

        if (!validate) { // This `!validate` check is for existing required fields
            button.setAttribute('disabled', true);
            
            window.theme.cart.addByForm(form).then(result => {
                button.removeAttribute('disabled');

                if (message) {
                    message.innerHTML = '';
                    message.classList.remove("enabled");
                }

                if (form.dataset.editForm) {
                    document.querySelector(`[data-edit-modal="${form.dataset.editForm}"]`).classList.remove('active');
                    
                    let qty = form.dataset.itemQty;
                    if (qty > 1) {
                        for (let i = 0; i <= qty - 1; i++) {
                            document.querySelector(`[data-minicart-item="${form.dataset.editForm}"] [data-quantity-selector-minus]`).click();
                        }
                    } else {
                        document.querySelector(`[data-minicart-item="${form.dataset.editForm}"] [data-quantity-selector-minus]`).click();
                    }
                }

                let closeQuickView = new CustomEvent('closeQuickView');
                window.dispatchEvent(closeQuickView);

            }).catch(error => {
                button.removeAttribute('disabled');

                if (message) {
                    message.innerHTML = '';
                    message.append(error.message);
                    message.classList.add("enabled");

                    if (window.screenMode == 'mobile') {
                        message.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
                    }
                }
            });
        }
    }

    OnVariantSelectorReady(event, product) {
        let select = event.target.querySelector('select');
        this.OnVariantSelectorChange(select, product);
    }

    OnVariantSelectorChange(select, product) {
        let outOfStockContent = this.querySelector('[data-outofstock-content]'),
            btn = this.getSubmitButton();

        if (!btn) {
            return;
        }

        // This block handles stock availability; our new function will handle size text
        if (product.available) {
            if (outOfStockContent)
                outOfStockContent.classList.remove('active');
            // btn.disabled = false; // We'll let updateButtonStateBasedOnSizeSelection handle this
        } else {
            if (outOfStockContent)
                outOfStockContent.classList.add('active');
            // btn.disabled = true; // We'll let updateButtonStateBasedOnSizeSelection handle this
        }

        // Check if the select variant changes
        select.addEventListener('change', event => {
            this.toggleButton(event.target, product);
            
            const sizeErrorMessage = this.querySelector('[data-error-message]');
            if (sizeErrorMessage) {
                sizeErrorMessage.innerHTML = '';
                sizeErrorMessage.classList.remove("enabled");
            }
            // Add call to update button text on variant change
            this.updateButtonStateBasedOnSizeSelection();
        });
        
        // Initial update after variant selector changes or is ready
        this.updateButtonStateBasedOnSizeSelection();
    }

    validateFieldRequired(form) {
        const fields = form.querySelectorAll("[required]");
        let foundError = false;

        for (let field of fields) {
            foundError = field.validity.valueMissing;
            if (foundError) {
                field.classList.add("invalid");
            }
        }
        return foundError;
    }

    /**
     * NEW FUNCTION: Updates the Add to Bag button text and state based on size selection.
     */
    updateButtonStateBasedOnSizeSelection() {
        const addToCartButton = this.getSubmitButton(); // Use the component's method
        const addToCartContent = addToCartButton ? addToCartButton.querySelector('[data-add-to-card-content]') : null;
        const soldOutContent = addToCartButton ? addToCartButton.querySelector('[data-outofstock-content]') : null;
        const sizeRadios = this.querySelectorAll('input[type="radio"][name="Size"]');

        if (!addToCartButton || !addToCartContent) {
            console.warn('Add to Bag button or its content div not found within component. Cannot update text.');
            return;
        }

        // --- NEW LOGIC START ---
        let isSizeOptionPresent = sizeRadios.length > 0; // Check if there are any size options at all
        let isSizeSelected = false;

        if (isSizeOptionPresent) {
            // If size options are present, check if one is actually selected
            for (let i = 0; i < sizeRadios.length; i++) {
                if (sizeRadios[i].checked) {
                    isSizeSelected = true;
                    break;
                }
            }
        } else {
            // If no size options are present, consider a size to be "selected" by default
            // (i.e., nothing for the user to choose, so it's ready to add to cart)
            isSizeSelected = true; 
        }
        // --- NEW LOGIC END ---


        // Check if the product is actually sold out. This should take precedence.
        const isSoldOutByTheme = soldOutContent && soldOutContent.classList.contains('active');

        if (isSoldOutByTheme) {
            if (soldOutContent) soldOutContent.style.display = 'block';
            if (addToCartContent) addToCartContent.style.display = 'none';
            addToCartButton.disabled = true;
            addToCartButton.classList.remove('active');
            // If sold out, the sold-out variant styling (line-through, etc.) is handled by CSS,
            // so we don't apply any button specific styling here.
            return; // Exit, sold out takes precedence
        }

        // If not sold out, handle size selection state based on NEW LOGIC
        if (isSizeSelected) { // This now correctly handles both "option picked" and "no options to pick"
            addToCartContent.textContent = 'Add to Bag';
            addToCartContent.style.display = 'block';
            if (soldOutContent) soldOutContent.style.display = 'none'; // Ensure sold out is hidden if size is selected
            addToCartButton.disabled = false;
            addToCartButton.classList.add('active'); // Re-enable its active styling/functionality
            
            // No inline styles here, CSS handles the active state.
        } else {
            // This 'else' block will only execute if isSizeOptionPresent is true BUT no radio is checked
            addToCartContent.textContent = 'Please select a size';
            addToCartContent.style.display = 'block';
            if (soldOutContent) soldOutContent.style.display = 'none'; // Ensure sold out is hidden
            addToCartButton.disabled = true;
            addToCartButton.classList.remove('active'); // Indicate it's not ready for purchase

            // Styling for this state is handled purely by custom-button-styles.css
        }
    }
}

customElements.define('component-addtocart', addToCartComponent);